import { featureMapForSpecimen, specimenFeatures, type FeatureMap } from './specimenFeatures';
import { getTaxon, listTaxa, type SpecimenTaxonomy } from './taxonomyDatabase';

export interface IdentificationCandidate {
  specimenId: string;
  commonName: string;
  scientificName: string;
  score: number;
  matchedFeatures: string[];
  conflictingFeatures: string[];
  observedFeatureCount: number;
}

export interface IdentificationResult {
  bestMatch: IdentificationCandidate | null;
  ranked: IdentificationCandidate[];
  ambiguous: boolean;
  feedback: string;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((value) => right.includes(value));
  return left === right;
}

function candidateScore(taxon: SpecimenTaxonomy, observations: FeatureMap): IdentificationCandidate {
  const knownFeatures = featureMapForSpecimen(taxon.id);
  const observed = Object.entries(observations).filter(([key, value]) => value !== undefined && key in knownFeatures);
  const matchedFeatures: string[] = [];
  const conflictingFeatures: string[] = [];
  let totalWeight = 0;
  let matchingWeight = 0;
  observed.forEach(([key, value]) => {
    const profile = specimenFeatures[taxon.id].features.find((item) => item.id === key);
    const diagnostic = profile?.diagnosticFor.some((rank) => [taxon.fiveKingdom, taxon.phylumOrDivision, taxon.class].includes(rank));
    const weight = diagnostic ? 2 : 1;
    totalWeight += weight;
    if (valuesEqual(knownFeatures[key], value)) { matchedFeatures.push(key); matchingWeight += weight; }
    else conflictingFeatures.push(key);
  });
  return {
    specimenId: taxon.id,
    commonName: taxon.commonName,
    scientificName: taxon.scientificName,
    score: totalWeight ? Math.round((matchingWeight / totalWeight) * 100) : 0,
    matchedFeatures,
    conflictingFeatures,
    observedFeatureCount: observed.length,
  };
}

/** Ranks taxa from observed evidence; missing observations are never treated as negative traits. */
export function identify(observations: FeatureMap, candidateIds?: string[]): IdentificationResult {
  const taxa = candidateIds ? candidateIds.map(getTaxon) : listTaxa({ includeReferenceOnly: true });
  const ranked = taxa.map((taxon) => candidateScore(taxon, observations)).sort((a, b) => b.score - a.score || b.matchedFeatures.length - a.matchedFeatures.length);
  if (!ranked.length || !Object.keys(observations).length) return { bestMatch: null, ranked, ambiguous: true, feedback: 'Record at least one observable feature before identifying the specimen.' };
  const bestMatch = ranked[0];
  const ambiguous = ranked.length > 1 && ranked[1].score === bestMatch.score;
  const feedback = ambiguous
    ? 'Several taxa fit the current evidence. Observe another diagnostic feature before deciding.'
    : bestMatch.conflictingFeatures.length
      ? `${bestMatch.matchedFeatures.length} observed feature(s) support this candidate; revisit ${bestMatch.conflictingFeatures.length} conflicting feature(s).`
      : `The observations support ${bestMatch.commonName}. Add more diagnostic evidence to increase confidence.`;
  return { bestMatch, ranked, ambiguous, feedback };
}
